import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import ContentPageLayout from "../components/ContentPageLayout";
import BoardPreview from "../components/BoardPreview";
import JsonLd, { breadcrumbList } from "../components/JsonLd";
import { STORYBOARD_TEMPLATE_PAGES } from "../content/storyboardTemplates";
import { trackEvent } from "../utils/analytics";

const StoryboardTemplatesIndexPage = () => {
  useEffect(() => {
    trackEvent("storyboard_templates_index_viewed");
  }, []);

  return (
    <ContentPageLayout>
      <JsonLd data={breadcrumbList([
        { name: "Storyboard maker", path: "/storyboard" },
        { name: "Storyboard templates", path: "/storyboard-templates" },
      ])} />
      <main style={styles.main}>
        <nav style={styles.breadcrumb} aria-label="Breadcrumb">
          <Link to="/storyboard" style={styles.inlineLink}>Storyboard maker</Link>
          <span style={styles.crumbSep}>/</span>
          <span>Templates</span>
        </nav>

        <h1 style={styles.title}>Free Storyboard Templates</h1>
        <p style={styles.subtitle}>
          Each template is a ready-made board: frames locked to the right aspect
          ratio, with notes boxes for shot type, camera move, duration, action,
          and dialogue. Open one in the{" "}
          <Link to="/storyboard" style={styles.inlineLink}>online storyboard maker</Link>{" "}
          to fill it in on screen, or download it as a blank PDF to print and
          sketch on.
        </p>

        <div style={styles.grid}>
          {STORYBOARD_TEMPLATE_PAGES.map((t) => (
            <Link key={t.slug} to={`/storyboard-templates/${t.slug}`} style={styles.card}>
              <BoardPreview seed={t.seed} title={`${t.title} preview`} />
              <h2 style={styles.cardTitle}>{t.title}</h2>
              <p style={styles.cardSummary}>{t.summary}</p>
              <span style={styles.more}>View template →</span>
            </Link>
          ))}
        </div>

        <section style={styles.help}>
          <h2 style={styles.helpTitle}>Which template should I use?</h2>
          <p style={styles.helpText}>
            Match the frame to where the video will be watched. Use{" "}
            <Link to="/storyboard-templates/16-9-widescreen" style={styles.inlineLink}>16:9</Link>{" "}
            for YouTube, TV, and most online video;{" "}
            <Link to="/storyboard-templates/2-39-cinemascope" style={styles.inlineLink}>2.39:1</Link>{" "}
            for a widescreen film look;{" "}
            <Link to="/storyboard-templates/vertical-9-16" style={styles.inlineLink}>9:16</Link>{" "}
            for Reels, TikTok, and Shorts; and the{" "}
            <Link to="/storyboard-templates/animation" style={styles.inlineLink}>animation template</Link>{" "}
            when you need timing notes beside every panel. Delivering more than one
            shape? Our guide to{" "}
            <Link to="/guides/storyboard-aspect-ratios" style={styles.inlineLink}>aspect ratios, letterboxing, and crop guides</Link>{" "}
            explains how to plan one shoot for several platforms.
          </p>
          <p style={styles.helpText}>
            First time storyboarding? Start with{" "}
            <Link to="/guides/what-is-a-storyboard" style={styles.inlineLink}>what a storyboard is and how to make one</Link>.
          </p>
        </section>
      </main>
    </ContentPageLayout>
  );
};

const styles = {
  main: {
    flex: 1,
    width: "100%",
    maxWidth: 1040,
    margin: "0 auto",
    padding: "40px 16px 56px",
    boxSizing: "border-box",
  },
  breadcrumb: {
    fontSize: 13,
    color: "#999",
    marginBottom: 20,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  crumbSep: { margin: "0 8px" },
  inlineLink: { color: "#00a4e4", textDecoration: "none" },
  title: {
    fontSize: "clamp(30px, 6vw, 42px)",
    fontWeight: 700,
    margin: "0 0 12px",
    color: "#1a1a1a",
  },
  subtitle: { fontSize: 17, color: "#555", maxWidth: 720, margin: "0 0 32px" },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
    gap: 20,
  },
  card: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    textDecoration: "none",
    color: "#1a1a1a",
    border: "2px solid #1a1a1a",
    background: "#fffdf6",
    padding: 14,
  },
  cardTitle: { fontSize: 18, fontWeight: 700, lineHeight: 1.3, margin: "6px 0 0" },
  cardSummary: { fontSize: 14, color: "#666", lineHeight: 1.55, margin: 0, flex: 1 },
  more: {
    fontSize: 13,
    fontWeight: 700,
    color: "#00a4e4",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  help: { marginTop: 48, paddingTop: 24, borderTop: "2px solid #1a1a1a", maxWidth: 760 },
  helpTitle: { fontSize: 24, fontWeight: 700, margin: "0 0 12px" },
  helpText: { fontSize: 16, color: "#333", lineHeight: 1.75, margin: "0 0 14px" },
};

export default StoryboardTemplatesIndexPage;
